import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, MapPin, Ticket, ChevronRight } from "lucide-react";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (d, t) => {
  if (!d) return "";
  const [y, m, day] = d.split("-").map(Number);
  const dt = new Date(y, m - 1, day);
  const str = dt.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return t ? `${str} · ${t}` : str;
};

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    axios.get(`${API}/events`)
      .then(r => setEvents(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <Header />
      {/* Hero */}
      <section className="bg-[#ff3131] py-16 px-4 text-center">
        <p className="text-white/70 text-sm uppercase tracking-widest mb-2">What's on</p>
        <h1 className="text-white text-5xl md:text-6xl font-bold" style={{ fontFamily: "Fredoka, cursive" }}>Events</h1>
        <p className="text-white/80 mt-3 text-base max-w-xl mx-auto">Workshops, holiday camps, and special sessions — book your spot below.</p>
      </section>

      <section className="max-w-5xl mx-auto px-4 py-12">
        {loading ? (
          <p className="text-center text-[#636E72] py-16">Loading events…</p>
        ) : events.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-[#ff3131]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-[#ff3131]" />
            </div>
            <h2 className="text-2xl font-bold text-[#2D3436] mb-2" style={{ fontFamily: "Fredoka, cursive" }}>No events yet</h2>
            <p className="text-[#636E72] text-sm">Check back soon — new events are added regularly.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map(ev => {
              const soldOut = ev.tickets_remaining === 0;
              return (
                <button key={ev.id}
                  onClick={() => nav(`/events/${ev.id}`)}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow text-left group"
                  data-testid={`event-card-${ev.id}`}>
                  {/* Image */}
                  <div className="relative h-48 bg-[#ff3131]/10 overflow-hidden">
                    {ev.image_data ? (
                      <img src={ev.image_data} alt={ev.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Calendar className="w-12 h-12 text-[#ff3131]/30" />
                      </div>
                    )}
                    {soldOut && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="bg-white text-[#2D3436] font-bold text-sm px-4 py-2 rounded-full">Sold Out</span>
                      </div>
                    )}
                    {!soldOut && ev.tickets_remaining <= 5 && ev.tickets_remaining > 0 && (
                      <div className="absolute top-3 right-3 bg-[#ff3131] text-white text-xs font-bold px-2.5 py-1 rounded-full">
                        Almost full!
                      </div>
                    )}
                  </div>
                  {/* Info */}
                  <div className="p-5">
                    <h2 className="font-bold text-[#2D3436] text-lg mb-2 leading-tight" style={{ fontFamily: "Fredoka, cursive" }}>{ev.title}</h2>
                    <div className="space-y-1.5 mb-4">
                      <p className="text-xs text-[#636E72] flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 flex-shrink-0" />{fmtDate(ev.event_date, ev.event_time)}
                      </p>
                      {ev.location && (
                        <p className="text-xs text-[#636E72] flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 flex-shrink-0" />{ev.location}
                        </p>
                      )}
                      <p className="text-xs text-[#636E72] flex items-center gap-1.5">
                        <Ticket className="w-3.5 h-3.5 flex-shrink-0" />
                        {soldOut ? "Sold out" : "Spaces available"}
                      </p>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-bold text-[#ff3131]" style={{ fontFamily: "Fredoka, cursive" }}>
                        {ev.ticket_price > 0 ? `£${ev.ticket_price.toFixed(2)}` : "Free"}
                      </span>
                      <span className={`flex items-center gap-1 text-sm font-bold ${soldOut ? "text-[#636E72]" : "text-[#ff3131]"}`}>
                        {soldOut ? "Sold Out" : "Book now"} <ChevronRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
